import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './listviewBumpUp.css';
import useResponsiveDesign from '../../../components/appComponents/useResponsiveDesign';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAnglesLeft, faAnglesRight, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

// FIREBASE
import { collection, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../../../firebase';

// IMPORT UI
import AppLoader from '../../../components/appComponents/AppLoader';

// IMAGES
import placeholder_color_thumbnail from '../../../assets/images/thumbnail/no-content-bumpups.jpg';
import youtube_logo from '../../../assets/images/logo/youtube-logo.png';
import youtube_white_logo from '../../../assets/images/logo/youtube-white-logo.png';
import bumpups_green_logo from '../../../assets/images/logo/bumpups-green-logo.png';

const ListViewBumpUp = ({ accessToken }) => {
  const [videos, setVideos] = useState([]);
  const [isContentAvailable, setIsContentAvailable] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);  // State for pagination
  const videosPerPage = 15;  // Videos per page
  const navigate = useNavigate();
  const device = useResponsiveDesign(); // Use the custom hook

  
  // Paint from the cache right away, then stay live on the user's bumps
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    const cacheKey = `videoCache_${user.uid}_BumpedVideos`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      setVideos(JSON.parse(cached));
      setIsContentAvailable(true);
      setIsLoading(false);
    }
    return onSnapshot(collection(db, 'users', user.uid, 'bumpContent'), snapshot => {
      const list = snapshot.docs
        .map(d => ({ ...d.data(), id: d.id.replace('bump-', '') }))
        .sort((a, b) => (b.video_upload_time?.seconds || 0) - (a.video_upload_time?.seconds || 0));
      setVideos(list);
      setIsContentAvailable(list.length > 0);
      setIsLoading(false);
      if (list.length) localStorage.setItem(cacheKey, JSON.stringify(list));
      else localStorage.removeItem(cacheKey);
    }, () => setIsLoading(false));
  }, []);

  const handleVideoClick = (videoId) => navigate(`/bumper/${videoId}`);

  const formatDate = (timestamp) => {
    if (!timestamp || !timestamp.seconds) return "";
    const date = new Date(timestamp.seconds * 1000);
    const formattedDate = date.toLocaleString('default', { month: 'long' }) + ' ' + date.getDate() + ', ' + date.getFullYear();
    return formattedDate;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return hours > 0
      ? `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
      : `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const truncateText = (text, maxLength) => {
    if (!text || text.length <= maxLength) {
      return text;
    }
    return text.substring(0, maxLength) + '...';
  };

  // Pagination logic
  const indexOfLastVideo = currentPage * videosPerPage;
  const indexOfFirstVideo = indexOfLastVideo - videosPerPage;
  const currentVideos = videos.slice(indexOfFirstVideo, indexOfLastVideo);
  const totalPages = Math.ceil(videos.length / videosPerPage);
  const startVideoNumber = indexOfFirstVideo + 1;
  const endVideoNumber = Math.min(indexOfLastVideo, videos.length);

  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const goToPreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  return (
    <div className="listbump-video-container">
        <div className="listbump-header-row">
          <div className="listbump-header-item">Videos</div>
          {(device === 'tablet' || device === 'desktop') && (
            <>
              <div className="listbump-header-item">Duration</div>
              <div className="listbump-header-item">Distribution</div>
              <div className="listbump-header-item">Quality</div>
              {device === 'desktop' && (
                <div className="listbump-header-item">Bumped</div>
              )}
            </>
          )}
        </div>
      {isLoading ? (
        <AppLoader inline />
      ) : isContentAvailable ? (
        <>
          {currentVideos.map((video) => (
            <div
              key={video.id}
              className="listbump-video-row"
              onClick={() => handleVideoClick(video.id)}
            >
              <div className="listbump-video-item">
                <img
                  src={video.video_thumbnail || placeholder_color_thumbnail}
                  alt="thumbnail"
                  className="listbump-thumbnail"
                />
                <div className="listbump-title-button-wrapper">
                  <div className="listbump-video-title">
                    {truncateText(video.publish_title || video.video_file_name, device === 'mobile' ? 22 : device === 'tablet' ? 40 : 54)}
                  </div>
                  <div className="listbump-video-description">
                    {formatDate(video.video_upload_time)}
                  </div>
                </div>
              </div>
              {(device === 'tablet' || device === 'desktop') && (
                <>
                  <div className="listbump-video-duration">{formatDuration(video.video_duration_in_seconds)}</div>
                  <div className="listbump-video-distribution">
                    {accessToken ? (
                      <img src={youtube_logo} alt="YouTube Icon" className="utubedistribution-icon-active" />
                    ) : (
                      <img src={youtube_white_logo} alt="YouTube Icon" className="utubedistribution-icon" />
                    )}
                  </div>
                  <div className="listbump-video-meta">{video.video_quality}</div>
                </>
              )}
              {device === 'desktop' && (
                <div className="listbump-video-meta">
                  <img src={bumpups_green_logo} alt="bumped logo" className="listbump-bumped-logo" />
                </div>
              )}
            </div>
          ))}
          {totalPages > 1 && (
            <div className="listbump-pagination-controls">
              <span className="listbump-pagination-info">
                {startVideoNumber}–{endVideoNumber} of {videos.length}
              </span>
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="listbump-pagination-button"
                aria-label="First page"
              >
                <FontAwesomeIcon icon={faAnglesLeft} />
              </button>
              <button
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
                className="listbump-pagination-button"
                aria-label="Previous page"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              <button
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                className="listbump-pagination-button"
                aria-label="Next page"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="listbump-pagination-button"
                aria-label="Last page"
              >
                <FontAwesomeIcon icon={faAnglesRight} />
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="listbump-empty">
          <img src={placeholder_color_thumbnail} alt="No content" className="listbump-empty-image" />
          <div className="listbump-empty-message">Upload a video to get started</div>
        </div>
      )}
    </div>
  );
};

export default ListViewBumpUp;
